import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row, Table } from 'react-bootstrap';
import { DatePicker, Select, Tooltip } from 'antd';
import type { Dayjs } from 'dayjs';
import AuthService from 'core/auth/auth.service';
import { filterOption } from 'shared/utils/common.utils';
import { getClFilters } from 'pages/private/management-tools/_redux/actions';
import { filtriClSelector } from 'pages/private/management-tools/_redux/selectors';
import { getUserInfo } from 'shared/_components/delegate-management/_redux/actions';
import { StorageKeysEnum } from 'enums/storageKeys.enum';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend, AreaChart, Area,
} from 'recharts';
import dayjs from 'dayjs';
import { AgendaCommercialeDashboardDto } from '@api/client';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import AppCard from 'shared/design-system/components/app-card';
import { getAgendaDashboard } from '../agenda-actions/_redux/actions';
import './styles.scss';

const KpiCard: React.FC<{
  icon: string;
  iconBg: string;
  label: string;
  value: number;
  sub?: string;
  variation?: number | null;
}> = ({ icon, iconBg, label, value, sub, variation }) => {
  const hasVar = variation != null && Number.isFinite(variation);
  const isUp = (variation ?? 0) >= 0;
  return (
    <div className="agenda-kpi-card">
      <div className="agenda-kpi-card__icon" style={{ backgroundColor: iconBg }}>
        <i className={icon} />
      </div>
      <div className="agenda-kpi-card__body">
        <div className="agenda-kpi-card__value">
          {value}
          {hasVar && (
            <span className={`agenda-kpi-card__var ${isUp ? 'is-up' : 'is-down'}`}>
              <i className={`fas fa-arrow-${isUp ? 'up' : 'down'}`} />
              {Math.abs(variation as number).toFixed(0)}%
            </span>
          )}
        </div>
        <div className="agenda-kpi-card__label">{label}</div>
        {sub && <div className="agenda-kpi-card__sub">{sub}</div>}
      </div>
    </div>
  );
};

const COLORS = ['#304BA2', '#304BA2', '#304BA2', '#304BA2', '#304BA2', '#304BA2', '#304BA2', '#304BA2'];
const PIE_COLORS = ['#304BA2', '#C83E8A', '#BFBFBF', '#5a73c5', '#dd6fa7', '#8f9bd0', '#3a5dcb'];

const AgendaDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const filtriCl = useAppSelector(filtriClSelector);
  const isAdmin = AuthService.isSuperAdmin();

  const businessUnits = useMemo(
    () => (isAdmin ? filtriCl?.businessUnit : filtriCl?.onlyChildBusinessUnit) || [],
    [isAdmin, filtriCl?.businessUnit, filtriCl?.onlyChildBusinessUnit],
  );

  const [selectedBuId, setSelectedBuId] = useState<number | undefined>(undefined);
  const [periodoDa, setPeriodoDa] = useState<Dayjs | null>(null);
  const [periodoA, setPeriodoA] = useState<Dayjs | null>(null);
  const [dashboardData, setDashboardData] = useState<AgendaCommercialeDashboardDto | undefined>(undefined);
  const [userInfo, setUserInfo] = useState<any>(() => AuthService.getUserInfoData());

  // BU di default dell'utente autenticato (presa da user-info)
  // Priorità: INDUSTRY > CLIENT > ACCOUNT > primo disponibile
  // Solo per utenti non admin: per admin si invia idStrutturaBusiness solo se selezionato nel filtro.
  const defaultUserBuId = useMemo<number | undefined>(() => {
    if (isAdmin) return undefined;
    const userStrutture: { id?: number; idStruttura?: { nome?: string } }[] =
      userInfo?.struttureBusiness || [];
    if (!userStrutture.length) return undefined;
    const findByType = (type: string) =>
      userStrutture.find((s) => s.idStruttura?.nome?.toLowerCase() === type)?.id;
    return findByType('industry') ?? findByType('client') ?? findByType('account') ?? userStrutture[0]?.id;
  }, [userInfo, isAdmin]);

  const effectiveBuId = selectedBuId ?? defaultUserBuId;

  useEffect(() => {
    if (!filtriCl?.businessUnit?.length && !filtriCl?.onlyChildBusinessUnit?.length) {
      dispatch(getClFilters());
    }
    if (!isAdmin && !userInfo?.struttureBusiness) {
      dispatch(getUserInfo()).unwrap().then((res) => {
        if (res) {
          localStorage.setItem(StorageKeysEnum.USER_INFO, JSON.stringify(res));
          setUserInfo(res);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!isAdmin && effectiveBuId == null) return; // per non-admin il BE richiede idStrutturaBusiness
    dispatch(
      getAgendaDashboard({
        agendaCommercialeDashboardInputDto: {
          ...(effectiveBuId != null ? { idStrutturaBusiness: effectiveBuId } : {}),
          ...(periodoDa ? { periodoDa: new Date(periodoDa.format('YYYY-MM-DD')) } : {}),
          ...(periodoA ? { periodoA: new Date(periodoA.format('YYYY-MM-DD')) } : {}),
        },
      }),
    )
      .unwrap()
      .then((res) => {
        if (res) setDashboardData(res);
      });
  }, [effectiveBuId, periodoDa, periodoA]);

  const stats = useMemo(() => {
    const now = dayjs();
    const total = dashboardData?.azioniTotali ?? 0;
    const lastWeek = dashboardData?.azioniUltimaSettimana ?? 0;
    const thisMonth = dashboardData?.azioniMeseCorrente ?? 0;

    // Variazione vs mese precedente: derivata da andamentoAttivita
    const andamento = dashboardData?.andamentoAttivita ?? [];
    const prev = now.subtract(1, 'month');
    const prevMonthEntry = andamento.find((a) => a.anno === prev.year() && a.mese === prev.month() + 1);
    const prevMonth = prevMonthEntry?.count ?? 0;
    const variationVsPrevMonth = prevMonth === 0
      ? (thisMonth > 0 ? 100 : null)
      : ((thisMonth - prevMonth) / prevMonth) * 100;

    const topClients = (dashboardData?.clientiPiuContattati ?? [])
      .slice()
      .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
      .slice(0, 20)
      .map((c) => ({
      name: c.nomeCliente || c.idCliente || '—',
      count: c.count ?? 0,
    }));

    const inactiveClients = (dashboardData?.clientiNonContattati ?? []).map(
      (c) => c.nomeCliente || c.idCliente || '',
    );

    const contactData = (dashboardData?.attivitaPerModalita ?? []).map((m) => ({
      name: m.modalita || '—',
      value: m.count ?? 0,
    }));

    // Serie andamento ultimi 3 mesi (mese corrente + 2 precedenti), riempiendo a 0 i mesi mancanti
    const monthlySeries: { name: string; count: number }[] = [];
    for (let i = 2; i >= 0; i -= 1) {
      const m = now.subtract(i, 'month');
      const entry = andamento.find((a) => a.anno === m.year() && a.mese === m.month() + 1);
      monthlySeries.push({ name: m.format('MMM YY'), count: entry?.count ?? 0 });
    }

    const upcomingByClient = (dashboardData?.prossimeAttivitaPerCliente ?? []).map((r) => ({
      clienteName: r.nomeCliente || r.idCliente || '—',
      ultimaDate: r.ultimaAttivitaDate,
      ultimaModalita: r.ultimaAttivitaModalita,
      prossimaDate: r.prossimaAttivitaDate,
      prossimaModalita: r.prossimaAttivitaModalita,
    }));

    return {
      total, lastWeek, thisMonth, variationVsPrevMonth,
      topClients, inactiveClients, contactData, monthlySeries, upcomingByClient,
    };
  }, [dashboardData]);

  return (
    <div className="agenda-dashboard mt-3">
      {/* Filtri dashboard */}
      <Row className="mb-3">
        <Col lg={4} md={6} sm={12}>
          <label className="agenda-kpi-card__label mb-1 d-block">{t('Struttura di business')}</label>
          <Select
            showSearch
            allowClear
            className="w-100"
            placeholder={t('Tutte le strutture')}
            optionFilterProp="children"
            filterOption={filterOption}
            value={selectedBuId}
            onChange={(v) => setSelectedBuId(v as number | undefined)}
          >
            {businessUnits.map((bu) => (
              <Select.Option key={bu.id} value={bu.id}>
                {bu.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col lg={4} md={6} sm={12}>
          <label className="agenda-kpi-card__label mb-1 d-block">{t('Periodo da')}</label>
          <DatePicker
            className="w-100"
            format="DD/MM/YYYY"
            allowClear
            placeholder={t('Seleziona data')}
            value={periodoDa}
            onChange={(d) => setPeriodoDa(d)}
            suffixIcon={<i className="far fa-calendar text-blue-links" />}
          />
        </Col>
        <Col lg={4} md={6} sm={12}>
          <label className="agenda-kpi-card__label mb-1 d-block">{t('Periodo a')}</label>
          <DatePicker
            className="w-100"
            format="DD/MM/YYYY"
            allowClear
            placeholder={t('Seleziona data')}
            value={periodoA}
            disabledDate={(d) => (periodoDa ? d.isBefore(periodoDa, 'day') : false)}
            onChange={(d) => setPeriodoA(d)}
            suffixIcon={<i className="far fa-calendar text-blue-links" />}
          />
        </Col>
      </Row>

      {/* KPI row */}
      <Row className="mb-4">
        <Col lg={4} md={6} sm={12} className="mb-3">
          <KpiCard
            icon="fas fa-calendar-check"
            iconBg="#304BA2"
            label={t('Azioni totali')}
            value={stats.total}
            sub={t('vs mese precedente')}
            variation={stats.variationVsPrevMonth}
          />
        </Col>
        <Col lg={4} md={6} sm={12} className="mb-3">
          <KpiCard
            icon="fas fa-calendar-week"
            iconBg="#C83E8A"
            label={t('Ultima settimana')}
            value={stats.lastWeek}
            sub={t('Ultimi 7 giorni')}
          />
        </Col>
        <Col lg={4} md={6} sm={12} className="mb-3">
          <KpiCard
            icon="fas fa-calendar-alt"
            iconBg="#BFBFBF"
            label={t('Mese corrente')}
            value={stats.thisMonth}
            sub={t('Conteggio del mese in corso')}
          />
        </Col>
      </Row>

      <Row>
        {/* Top clients chart */}
        <Col lg={7} md={12} className="mb-4">
          <AppCard
            title={t('Clienti più contattati (Top 20)')}
            headerElement={
              <Tooltip title={t('Clienti con il maggior numero di azioni registrate')}>
                <i className="fas fa-circle-info text-blue-links ms-2" style={{ fontSize: '0.85rem' }} />
              </Tooltip>
            }
          >
            {stats.topClients.length === 0 ? (
              <div className="agenda-dashboard__empty">
                <i className="fas fa-chart-bar" />
                <span>{t('Nessun dato disponibile')}</span>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={stats.topClients}
                  layout="vertical"
                  margin={{ top: 4, right: 24, left: 8, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={220}
                    tick={{ fontSize: 11 }}
                  />
                  <ReTooltip
                    formatter={(val: number) => [val, t('Azioni')]}
                    contentStyle={{ fontSize: '0.82rem', borderRadius: 6 }}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={28}>
                    {stats.topClients.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </AppCard>
        </Col>

        {/* Inactive clients */}
        <Col lg={5} md={12} className="mb-4">
          <AppCard
            title={t('Clienti non contattati')}
            headerElement={
              <Tooltip title={t('Clienti presenti in anagrafica senza alcuna azione registrata')}>
                <i className="fas fa-circle-info text-blue-links ms-2" style={{ fontSize: '0.85rem' }} />
              </Tooltip>
            }
          >
            {stats.inactiveClients.length === 0 ? (
              <div className="agenda-dashboard__empty agenda-dashboard__empty--success">
                <i className="fas fa-check-circle" />
                <span>{t('Tutti i clienti sono stati contattati!')}</span>
              </div>
            ) : (
              <div className="agenda-inactive-list">
                <div className="agenda-inactive-list__count">
                  <span className="agenda-inactive-list__number">{stats.inactiveClients.length}</span>
                  <span className="agenda-inactive-list__text">
                    {stats.inactiveClients.length === 1
                      ? t('cliente senza attività')
                      : t('clienti senza attività')}
                  </span>
                </div>
                <div className="agenda-inactive-list__items">
                  {stats.inactiveClients.map((name, i) => (
                    <span key={i} className="agenda-inactive-chip">
                      <i className="fas fa-user-slash me-1" />
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </AppCard>
        </Col>
      </Row>

      {/* Modalità contatto + Andamento nel tempo */}
      <Row>
        <Col lg={5} md={12} className="mb-4">
          <AppCard
            title={t('Attività per modalità di contatto')}
            headerElement={
              <Tooltip title={t('Distribuzione delle azioni in base alla modalità di contatto utilizzata')}>
                <i className="fas fa-circle-info text-blue-links ms-2" style={{ fontSize: '0.85rem' }} />
              </Tooltip>
            }
          >
            {stats.contactData.length === 0 ? (
              <div className="agenda-dashboard__empty">
                <i className="fas fa-chart-pie" />
                <span>{t('Nessun dato disponibile')}</span>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={stats.contactData}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={95}
                    innerRadius={45}
                    paddingAngle={2}
                    label={(entry: { name?: string; percent?: number }) =>
                      `${entry.name ?? ''} (${((entry.percent ?? 0) * 100).toFixed(0)}%)`
                    }
                    labelLine={false}
                  >
                    {stats.contactData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <ReTooltip
                    formatter={(val: number, name: string) => [val, name]}
                    contentStyle={{ fontSize: '0.82rem', borderRadius: 6 }}
                  />
                  <Legend wrapperStyle={{ fontSize: '0.78rem' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </AppCard>
        </Col>

        <Col lg={7} md={12} className="mb-4">
          <AppCard
            title={t('Andamento attività (ultimi 3 mesi)')}
            headerElement={
              <Tooltip title={t('Numero di azioni registrate ogni mese negli ultimi 3 mesi')}>
                <i className="fas fa-circle-info text-blue-links ms-2" style={{ fontSize: '0.85rem' }} />
              </Tooltip>
            }
          >
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={stats.monthlySeries} margin={{ top: 8, right: 24, left: 0, bottom: 4 }}>
                <defs>
                  <linearGradient id="agendaAreaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#304BA2" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#304BA2" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <ReTooltip
                  formatter={(val: number) => [val, t('Azioni')]}
                  contentStyle={{ fontSize: '0.82rem', borderRadius: 6 }}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#304BA2"
                  strokeWidth={2}
                  fill="url(#agendaAreaGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </AppCard>
        </Col>
      </Row>

      {/* Prossime attività per cliente */}
      <Row>
        <Col xs={12} className="mb-4">
          <AppCard
            title={t('Prossime attività per cliente')}
            headerElement={
              <Tooltip title={t('Per ogni cliente: ultima attività registrata e prossima attività pianificata')}>
                <i className="fas fa-circle-info text-blue-links ms-2" style={{ fontSize: '0.85rem' }} />
              </Tooltip>
            }
          >
            {stats.upcomingByClient.length === 0 ? (
              <div className="agenda-dashboard__empty">
                <i className="fas fa-calendar-day" />
                <span>{t('Nessuna prossima attività pianificata')}</span>
              </div>
            ) : (
              <div className="table-responsive table-bordered">
                <Table className="mt-0 mb-0 agenda-dashboard-table">
                  <thead>
                    <tr>
                      <th>{t('Cliente')}</th>
                      <th>{t('Ultima attività')}</th>
                      <th>{t('Prossima attività')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.upcomingByClient.map((row, i) => (
                      <tr key={i}>
                        <td className="fw-semibold">{row.clienteName}</td>
                        <td>
                          {row.ultimaDate ? (
                            <span>
                              {dayjs(row.ultimaDate).format('DD/MM/YYYY')}
                              {row.ultimaModalita && (
                                <span className="text-muted ms-2" style={{ fontSize: '0.78rem' }}>
                                  · {row.ultimaModalita}
                                </span>
                              )}
                            </span>
                          ) : '—'}
                        </td>
                        <td>
                          {row.prossimaDate ? (
                            <span className="fw-semibold">
                              {dayjs(row.prossimaDate).format('DD/MM/YYYY')}
                              {row.prossimaModalita && (
                                <span className="text-muted ms-2" style={{ fontSize: '0.78rem' }}>
                                  · {row.prossimaModalita}
                                </span>
                              )}
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </AppCard>
        </Col>
      </Row>

    </div>
  );
};

export default AgendaDashboard;
